// This app's RTK Query cache policy, applied to @bambi/api-client's
// singleton. api-client stays spec-shaped (one tag per operation, plus the
// overrides in api-client/splits.ts — both coupled the same below); what a
// mutation invalidates beyond its own resource is an app concern, so it
// lives here. The invalidation group below is the rule legacy
// post-process.ts applies at codegen time.
import { bambiApi as specApi } from '@bambi/api-client';
import '@bambi/api-client/assignments';
import '@bambi/api-client/bambi';
import '@bambi/api-client/routes';
import '@bambi/api-client/trips';
import type { UnknownAction } from '@reduxjs/toolkit';

import { enhancedBambiApi as legacyApi } from 'src/common/external/bambi-api/enhancedBambiApi';

export type CountTag =
  | 'dispatch-summary'
  | 'requested-trip-count'
  | 'will-call-count';

export const COUNT_TAGS = [
  'dispatch-summary',
  'requested-trip-count',
  'will-call-count',
] as const satisfies readonly CountTag[];

// An override replaces the generated list rather than merging into it, so
// an override that wants the group has to restate it; naming it keeps the
// two from drifting.
export const TRIP_MUTATION_TAGS = [
  'trips',
  'dispatch',
  'run-bambi-run',
] as const;

type Tags = readonly string[];
type TagPatch = { providesTags?: Tags; invalidatesTags?: Tags };
// What enhanceEndpoints hands a function-form entry: the live definition,
// or undefined when nothing injected that name.
type Lifecycle = {
  dispatch: (action: UnknownAction) => unknown;
  queryFulfilled: Promise<{ data: unknown; meta: unknown }>;
};
type Definition =
  | {
      providesTags?: unknown;
      invalidatesTags?: unknown;
      onQueryStarted?: (arg: unknown, api: Lifecycle) => unknown;
    }
  | undefined;
type Patch = (definition: Definition) => void;

// Only the single-tag lists ['trips'], ['dispatch'], ['run-bambi-run'] are
// widened — a list that already names more is a deliberate override.
const coupleInvalidation: Patch = (definition) => {
  if (!definition) {
    return;
  }
  const current = definition.invalidatesTags;
  if (
    Array.isArray(current) &&
    current.length === 1 &&
    (TRIP_MUTATION_TAGS as Tags).includes(current[0])
  ) {
    definition.invalidatesTags = [...TRIP_MUTATION_TAGS];
  }
};

// 'dispatch-summary' replaces 'dispatch' for a delta mutation; it never adds
// to the group, so it's excluded here rather than typechecking as if it did.
type MutationCountTag = Exclude<CountTag, 'dispatch-summary'>;

const alsoInvalidates = (...extra: readonly MutationCountTag[]): TagPatch => ({
  invalidatesTags: [...TRIP_MUTATION_TAGS, ...extra],
});

// Keyed by operationId. Assign/reassign/unassign and their bulk forms use
// makeDispatchDeltaEndpoint/makeBulkUnassignDeltaEndpoint's non-mergeable
// fallback (group + that mutation's counts); onQueryStarted ships later.
const OVERRIDES: Record<string, TagPatch | undefined> = {
  // Each count gets its own tag so only the mutations that can change it
  // refetch it (bambi-api/README.md, "Over-invalidation").
  tripsRequestedCountRetrieve: { providesTags: ['requested-trip-count'] },
  tripsActivatedWillCallCountRetrieve: { providesTags: ['will-call-count'] },
  tripsAcceptPartialUpdate: alsoInvalidates('requested-trip-count'),
  tripsBulkAcceptCreate: alsoInvalidates('requested-trip-count'),
  tripsRejectPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsBulkRejectCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsUnrejectPartialUpdate: alsoInvalidates('requested-trip-count'),
  tripsOverrideStatusPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsActivateWillCallPartialUpdate: alsoInvalidates('will-call-count'),
  tripsCancelPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsAcceptCancellationRequestPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsBulkCancelCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsUncancelPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsDestroy: alsoInvalidates('requested-trip-count', 'will-call-count'),
  tripsPartialUpdate: alsoInvalidates('will-call-count'),
  tripsUpdatePickupAtPartialUpdate: alsoInvalidates('will-call-count'),
  // Non-mergeable fallback (see above): group plus both counts.
  tripsAssignPartialUpdate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  tripsBulkAssignCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  // Non-mergeable fallback (see above): group only, no counts.
  tripsReassignPartialUpdate: { invalidatesTags: [...TRIP_MUTATION_TAGS] },
  tripsBulkReassignCreate: { invalidatesTags: [...TRIP_MUTATION_TAGS] },
  // Non-mergeable fallback (see above): group plus will-call only.
  tripsUnassignPartialUpdate: alsoInvalidates('will-call-count'),
  tripsBulkUnassignCreate: alsoInvalidates('will-call-count'),
  // Validation and pricing change no data. Invalidating here refetched grids
  // for nothing and raced /dispatch/ against the assign that follows (BAM-793).
  tripsValidatePartialUpdate: { invalidatesTags: [] },
  tripsBulkValidateCreate: { invalidatesTags: [] },
  tripsBulkPriceCreate: { invalidatesTags: [] },
  tripsMultiloadCreate: { invalidatesTags: ['dispatch', 'trips'] },
  tripsMultiloadPartialUpdate: { invalidatesTags: ['dispatch', 'trips'] },
  tripsMultiloadDestroy: { invalidatesTags: ['dispatch', 'trips'] },
  // The board does NOT provide 'trips' (bambi-api/README.md, "The tags");
  // 'assignments' stays so crew/break/transfer mutations refetch the halves.
  dispatchAssignedRetrieve: { providesTags: ['dispatch', 'assignments'] },
  dispatchUnassignedRetrieve: { providesTags: ['dispatch', 'assignments'] },
  // Keeps 'dispatch' so mutations that still refetch the board refresh the
  // counts; 'dispatch-summary' is what delta-carrying mutations invalidate.
  dispatchSummaryRetrieve: { providesTags: ['dispatch', 'dispatch-summary'] },
  runBambiRunApplyCreate: alsoInvalidates(
    'requested-trip-count',
    'will-call-count'
  ),
  routesRouteBambiRouteCreate: {
    invalidatesTags: [
      'routes',
      ...TRIP_MUTATION_TAGS,
      'requested-trip-count',
      'will-call-count',
    ],
  },
};

// RTK's own shapes for invalidatesTags: a static list, or a function of
// the settled request.
type TagsDescription =
  | Tags
  | ((result: unknown, error: unknown, arg: unknown, meta: unknown) => Tags);
type LegacyTag = Parameters<typeof legacyApi.util.invalidateTags>[0][number];

// Transitional, gone with the legacy client: the two clients have separate
// tag registries, so an api-client mutation has to tell the legacy client
// what it invalidated or legacy queries that still provide the tag (the
// dispatch halves, 'assignments') go stale. Settled = fulfilled or
// rejected-with-value, RTK's own rule; the legacy middleware then scopes and
// defers the refetches exactly as it does for its own mutations. One-way: no
// api-client query yet provides a tag a legacy mutation invalidates.
const mirrorInvalidation: Patch = (definition) => {
  if (!definition || definition.invalidatesTags === undefined) {
    return;
  }
  const described = definition.invalidatesTags as TagsDescription;
  const inner = definition.onQueryStarted;
  definition.onQueryStarted = async (arg, api) => {
    void inner?.(arg, api);
    let result: unknown;
    let error: unknown;
    let meta: unknown;
    try {
      ({ data: result, meta } = await api.queryFulfilled);
    } catch (rejection) {
      const settled = rejection as {
        error: unknown;
        isUnhandledError: boolean;
        meta: unknown;
      };
      if (settled.isUnhandledError) {
        return;
      }
      ({ error, meta } = settled);
    }
    const tags =
      typeof described === 'function'
        ? described(result, error, arg, meta)
        : described;
    if (tags.length > 0) {
      api.dispatch(legacyApi.util.invalidateTags(tags as LegacyTag[]));
    }
  };
};

// Only covers the four imports above; a future split emitting a coupled
// single-tag mutation is silently left uncoupled — the throw below guards
// OVERRIDES keys only.
const policyFor =
  (name: string): Patch =>
  (definition) => {
    const override = OVERRIDES[name];
    if (override) {
      // Function form on purpose: the object form silently no-ops when
      // nothing injected `name` (a split import missing above). This throws.
      if (!definition) {
        throw new Error(
          `cachePolicy: ${name} is not an injected endpoint; import its @bambi/api-client split above`
        );
      }
      Object.assign(definition, override);
    } else {
      coupleInvalidation(definition);
    }
    mirrorInvalidation(definition);
  };

const patches: Record<string, Patch> = Object.fromEntries(
  [
    ...new Set([...Object.keys(specApi.endpoints), ...Object.keys(OVERRIDES)]),
  ].map((name) => [name, policyFor(name)])
);

export const bambiApi = specApi.enhanceEndpoints({
  addTagTypes: COUNT_TAGS,
  endpoints: patches,
});
